import React, { useEffect, useMemo, useRef, useState } from "react";
import PropTypes from "prop-types";
import createDOMPurify from "dompurify";
import { createPortal } from "react-dom";
import { Marker, Tooltip } from "react-leaflet";
import L from "leaflet";

const START_TYPE_ID = 123;
const DEFAULT_MARKER_COLOR = "#3B8EDA";
const START_MARKER_COLOR = "#C83732";
const ROUTE_YOU_MARKER_SIZE = 26;

let domPurifyInstance = null;
const routeYouPoiIconCache = new Map();

const getDomPurify = () => {
  if (globalThis.window === undefined) {
    return null;
  }

  if (!domPurifyInstance) {
    domPurifyInstance = createDOMPurify(globalThis.window);
  }

  return domPurifyInstance;
};

const parseWktPoint = (wkt) => {
  if (!wkt) return null;
  const match = wkt.match(/POINT\(\s*([-\d.]+)\s+([-\d.]+)\s*\)/);
  if (!match) return null;
  const lng = Number.parseFloat(match[1]);
  const lat = Number.parseFloat(match[2]);
  if (Number.isNaN(lng) || Number.isNaN(lat)) return null;
  return [lat, lng];
};

const genericIcon = L.divIcon({
  className: "poi-marker-icon",
  iconSize: [24, 24],
  iconAnchor: [12, 24],
  html: '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="24" height="24"><path d="M12 0C7.6 0 4 3.6 4 8c0 5.4 8 16 8 16s8-10.6 8-16c0-4.4-3.6-8-8-8zm0 11a3 3 0 110-6 3 3 0 010 6z" fill="#4A90D9"/></svg>',
});

const getPoiMarkerColor = (typeId) => {
  if (typeId === START_TYPE_ID) {
    return START_MARKER_COLOR;
  }

  return DEFAULT_MARKER_COLOR;
};

const getPoiIcon = (typeId) => {
  const typeIconUrl = getPoiTypeIconUrl(typeId);

  if (!typeIconUrl) {
    return genericIcon;
  }

  const markerColor = getPoiMarkerColor(typeId);
  const cacheKey = `${typeId}-${markerColor}`;

  if (routeYouPoiIconCache.has(cacheKey)) {
    return routeYouPoiIconCache.get(cacheKey);
  }

  const icon = L.divIcon({
    className: "poi-marker-icon poi-marker-icon--routeyou",
    iconSize: [ROUTE_YOU_MARKER_SIZE, ROUTE_YOU_MARKER_SIZE],
    iconAnchor: [ROUTE_YOU_MARKER_SIZE / 2, ROUTE_YOU_MARKER_SIZE / 2],
    html: `<span class="poi-marker-icon__surface" style="--poi-marker-color: ${markerColor}; --poi-marker-icon: url('${typeIconUrl}');"><span class="poi-marker-icon__glyph"></span></span>`,
  });

  routeYouPoiIconCache.set(cacheKey, icon);

  return icon;
};

const getPoiLabel = (poi) => {
  if (poi.location?.name?.nl) return poi.location.name.nl;
  if (poi.location?.type?.name?.nl) return poi.location.type.name.nl;
  return "POI";
};

const normalizeText = (value) => {
  const normalizedValue = value?.replaceAll(/\s+/g, " ").trim();

  return normalizedValue || null;
};

const getAdjacentMeaningfulSibling = (node, direction) => {
  let sibling =
    direction === "previous" ? node.previousSibling : node.nextSibling;

  while (sibling?.nodeType === 3 && !normalizeText(sibling.textContent)) {
    const emptyTextSibling = sibling;
    sibling =
      direction === "previous" ? sibling.previousSibling : sibling.nextSibling;
    emptyTextSibling.remove();
  }

  return sibling;
};

const removeAdjacentBreak = (node, direction) => {
  const sibling = getAdjacentMeaningfulSibling(node, direction);

  if (sibling?.nodeType === 1 && sibling.nodeName === "BR") {
    sibling.remove();
  }
};

const isDuplicatePoiInfoLink = (href, locationId, textId) => {
  if (!href || !locationId) {
    return false;
  }

  try {
    const parsedUrl = new URL(href, "https://www.routeyou.com");
    const locationMatch = parsedUrl.pathname.match(/\/location\/view\/(\d+)/);

    if (!locationMatch || locationMatch[1] !== String(locationId)) {
      return false;
    }

    const linkTextId = parsedUrl.searchParams.get("toptext");

    if (textId && linkTextId && linkTextId !== String(textId)) {
      return false;
    }

    return true;
  } catch {
    return false;
  }
};

const getPoiDescriptionHtml = (poi) => {
  const html = poi?.text?.description?.nl;
  if (!html) {
    return null;
  }

  const domPurify = getDomPurify();
  const sanitizedHtml = domPurify ? domPurify.sanitize(html) : html;

  if (globalThis.document === undefined) {
    return sanitizedHtml || null;
  }

  const tempElement = globalThis.document.createElement("div");
  tempElement.innerHTML = sanitizedHtml;

  tempElement.querySelectorAll("a").forEach((link) => {
    if (isDuplicatePoiInfoLink(link.getAttribute("href"), poi?.location?.id, poi?.text?.id)) {
      removeAdjacentBreak(link, "previous");
      removeAdjacentBreak(link, "next");
      link.remove();
      return;
    }

    link.setAttribute("target", "_blank");
    link.setAttribute("rel", "noopener noreferrer");
  });

  const textContent = normalizeText(
    tempElement.textContent || tempElement.innerText || ""
  );

  if (!textContent) {
    return null;
  }

  return tempElement.innerHTML;
};

const getPoiImageUrl = (poi) => {
  return poi?.text?.media?.url?.nl || null;
};

const getPoiTypeIconUrl = (typeId) => {
  if (!typeId) {
    return null;
  }

  return `https://media.routeyou.com/poi-type/1/${typeId}.svg`;
};

const getPoiInfoUrl = (poi) => {
  const locationId = poi?.location?.id;
  const textId = poi?.text?.id;

  if (!locationId) {
    return null;
  }

  if (textId) {
    return `https://www.routeyou.com/nl-be/location/view/${locationId}?toptext=${textId}`;
  }

  return `https://www.routeyou.com/nl-be/location/view/${locationId}`;
};

const hideBrokenMedia = (event) => {
  event.currentTarget.style.display = "none";

  if (event.currentTarget.parentElement) {
    event.currentTarget.parentElement.style.display = "none";
  }
};

const PoiMarkers = ({ pois }) => {
  const [selectedPoi, setSelectedPoi] = useState(null);
  const closeButtonRef = useRef(null);

  useEffect(() => {
    const documentElement = globalThis.document?.documentElement;
    const bodyElement = globalThis.document?.body;

    if (!documentElement || !bodyElement) {
      return undefined;
    }

    if (selectedPoi) {
      documentElement.classList.add("u-noscroll");
      bodyElement.classList.add("u-noscroll");
    } else {
      documentElement.classList.remove("u-noscroll");
      bodyElement.classList.remove("u-noscroll");
    }

    return () => {
      documentElement.classList.remove("u-noscroll");
      bodyElement.classList.remove("u-noscroll");
    };
  }, [selectedPoi]);

  useEffect(() => {
    if (!selectedPoi || globalThis.document === undefined) {
      return undefined;
    }

    const handleKeyDown = (event) => {
      if (event.key === "Escape") {
        setSelectedPoi(null);
      }
    };

    globalThis.document.addEventListener("keydown", handleKeyDown);

    return () => {
      globalThis.document.removeEventListener("keydown", handleKeyDown);
    };
  }, [selectedPoi]);

  useEffect(() => {
    if (selectedPoi && closeButtonRef.current) {
      closeButtonRef.current.focus();
    }
  }, [selectedPoi]);

  const closePoiModal = () => {
    setSelectedPoi(null);
  };

  const selectedPoiData = useMemo(() => {
    if (!selectedPoi) {
      return null;
    }

    const label = getPoiLabel(selectedPoi);
    const typeId = selectedPoi.location?.type?.id;
    const typeName = selectedPoi.location?.type?.name?.nl || null;
    const locationId = selectedPoi.location?.id || "selected";

    return {
      descriptionHtml: getPoiDescriptionHtml(selectedPoi),
      imageUrl: getPoiImageUrl(selectedPoi),
      infoUrl: getPoiInfoUrl(selectedPoi),
      label,
      titleId: `poi-modal-title-${locationId}`,
      typeIconUrl: getPoiTypeIconUrl(typeId),
      typeName: typeName && typeName !== label ? typeName : null,
    };
  }, [selectedPoi]);

  const modalMarkup =
    selectedPoiData && globalThis.document?.body
      ? createPortal(
          <div className="poi-modal">
            <button
              type="button"
              className="poi-modal__backdrop"
              onClick={closePoiModal}
              aria-label="POI sluiten"
            ></button>
            <dialog
              className="poi-modal__dialog"
              open
              aria-labelledby={selectedPoiData.titleId}
            >
              <button
                ref={closeButtonRef}
                type="button"
                className="poi-modal__close"
                onClick={closePoiModal}
                aria-label="POI sluiten"
              >
                ×
              </button>

              <div className="poi-modal__panel">
                <div className="poi-modal__header">
                  {selectedPoiData.typeIconUrl && (
                    <span className="poi-modal__type-icon">
                      <img
                        src={selectedPoiData.typeIconUrl}
                        alt=""
                        loading="lazy"
                        onError={hideBrokenMedia}
                      />
                    </span>
                  )}

                  <div className="poi-modal__titles">
                    <h2 id={selectedPoiData.titleId} className="poi-modal__name">
                      {selectedPoiData.label}
                    </h2>
                    {selectedPoiData.typeName && (
                      <span className="poi-modal__type">
                        {selectedPoiData.typeName}
                      </span>
                    )}
                  </div>
                </div>

                {selectedPoiData.imageUrl && (
                  <a
                    className="poi-modal__image-link"
                    href={selectedPoiData.imageUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <img
                      className="poi-modal__image"
                      src={selectedPoiData.imageUrl}
                      alt={selectedPoiData.label}
                      loading="lazy"
                      onError={hideBrokenMedia}
                    />
                  </a>
                )}

                {selectedPoiData.descriptionHtml && (
                  <div
                    className="poi-modal__description"
                    dangerouslySetInnerHTML={{
                      __html: selectedPoiData.descriptionHtml,
                    }}
                  ></div>
                )}

                {selectedPoiData.infoUrl && (
                  <div className="poi-modal__footer">
                    <a
                      className="poi-modal__link"
                      href={selectedPoiData.infoUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      Meer informatie op RouteYou
                    </a>
                  </div>
                )}
              </div>
            </dialog>
          </div>,
          globalThis.document.body
        )
      : null;

  if (!pois?.length) return null;

  return (
    <>
      {pois.map((poi, index) => {
        const position = parseWktPoint(poi.location?.centroid?.wkt);
        if (!position) return null;

        const typeId = poi.location?.type?.id;
        const label = getPoiLabel(poi);

        return (
          <Marker
            key={poi.location?.id || index}
            position={position}
            icon={getPoiIcon(typeId)}
            eventHandlers={{
              click: () => {
                setSelectedPoi(poi);
              },
            }}
          >
            <Tooltip direction="top" offset={[0, -12]}>
              {label}
            </Tooltip>
          </Marker>
        );
      })}
      {modalMarkup}
    </>
  );
};

PoiMarkers.propTypes = {
  pois: PropTypes.arrayOf(PropTypes.object),
};

export default PoiMarkers;
